import { useState, useEffect } from "react";
import { ComplianceBadge } from "@/components/ui/compliance-badge";
import { Button } from "@/components/ui/button";
import type { ComplianceScore } from "@/lib/api/types";
import { cn } from "@/lib/utils";
import { RefreshCw, AlertTriangle, AlertCircle, ShieldCheck, Loader2, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const tierLabels: Record<string, string> = {
  pass: "Pass",
  advisory: "Advisory",
  "critical-fail": "Critical Fail",
};

function dimensionLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

const dimensionGuidance: Record<string, string> = {
  titleQuality: "Use a clear, descriptive title that summarizes the epic scope",
  problemDepth: "Expand the problem statement with specific details, evidence, and user impact",
  solutionDepth: "Add technical specifics, implementation steps, and architectural details to the solution",
  impactClarity: "Quantify the expected impact with metrics, user counts, or efficiency gains",
  descriptionQuality: "Ensure the markdown description is well-structured with clear sections and formatting",
  optionalCoverage: "Add alternatives considered, dependencies, success metrics, and effort estimates",
};

interface IssueCardProps {
  issue: { code: string; message: string; dimension: string; suggestion?: string };
  variant: "critical" | "advisory";
}

function IssueCard({ issue, variant }: IssueCardProps) {
  const isCritical = variant === "critical";
  return (
    <div
      className={cn(
        "rounded-lg border p-3 space-y-1",
        isCritical
          ? "border-red-800/30 bg-red-900/[0.06]"
          : "border-yellow-800/30 bg-yellow-900/[0.06]"
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "font-mono text-xs font-medium",
            isCritical ? "text-red-400" : "text-yellow-400"
          )}
        >
          {issue.code}
        </span>
        <span className="text-xs text-muted-foreground">{dimensionLabel(issue.dimension)}</span>
      </div>
      <p className="text-sm">{issue.message}</p>
      {issue.suggestion && (
        <p className="text-xs text-muted-foreground italic">{issue.suggestion}</p>
      )}
    </div>
  );
}

export interface ComplianceDetailSectionProps {
  score: ComplianceScore | null | undefined;
  onReaudit?: () => void;
  isAuditing?: boolean;
  defaultExpanded?: boolean;
  identifier?: string;
  isRefreshing?: boolean;
}

export function ComplianceDetailSection({
  score,
  onReaudit,
  isAuditing,
  defaultExpanded = false,
  identifier,
  isRefreshing,
}: ComplianceDetailSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [animateBars, setAnimateBars] = useState(false);

  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => { setAnimateBars(true); }, 50);
      return () => { clearTimeout(timer); };
    }
    setAnimateBars(false);
    return undefined;
  }, [isExpanded]);

  // Null / not-yet-audited state
  if (!score) {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
            <div>
              <span className="text-sm text-muted-foreground font-medium">Not Yet Audited</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                This request needs a compliance audit before it can be approved.
              </p>
            </div>
          </div>
          {onReaudit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReaudit}
              disabled={isAuditing}
            >
              {isAuditing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Auditing…
                </>
              ) : (
                "Audit Now"
              )}
            </Button>
          )}
        </div>
        {identifier && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-1">Or improve &amp; re-audit via CLI:</p>
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
              @request-formulator improve &quot;{identifier}&quot;
            </code>
          </div>
        )}
      </div>
    );
  }

  const dimensionEntries = Object.entries(score.dimensions);
  const allPassing = dimensionEntries.length > 0 && dimensionEntries.every(([, v]) => v >= 80);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      {/* Header: toggle + re-audit as siblings to avoid nested buttons */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-3 min-w-0 cursor-pointer select-none text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          onClick={() => { setIsExpanded(!isExpanded); }}
          aria-expanded={isExpanded}
          aria-controls="compliance-detail-content"
        >
          <ChevronRight
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
              isExpanded && "rotate-90"
            )}
          />
          <ComplianceBadge score={score} showScore className="text-sm" />
          <span className="text-sm text-muted-foreground truncate">
            {score.overall}/100 — {tierLabels[score.tier] ?? score.tier}
          </span>
          {isRefreshing && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground ml-2">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
              Refreshing
            </span>
          )}
        </button>
        {onReaudit && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 ml-2"
            onClick={onReaudit}
            disabled={isAuditing}
            aria-label="Re-audit compliance"
          >
            {isAuditing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            <span className="ml-1.5 hidden sm:inline">Re-audit</span>
          </Button>
        )}
      </div>

      {/* Compact all-passing indicator */}
      {allPassing && !isExpanded && (
        <div className="flex items-center gap-2 mt-2 text-xs text-green-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>All dimensions passing</span>
          <button
            type="button"
            onClick={() => { setIsExpanded(true); }}
            className="text-muted-foreground hover:text-foreground underline ml-1"
          >
            Show details
          </button>
        </div>
      )}

      {/* Collapsible content with smooth grid-rows transition */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        aria-hidden={!isExpanded}
        id="compliance-detail-content"
      >
        <div className="overflow-hidden">
          <div className="space-y-4 pt-4">
            {/* Dimension progress bars */}
            {dimensionEntries.length > 0 && (
              <div className="space-y-2">
                {dimensionEntries.map(([dim, val]) => (
                  <TooltipProvider key={dim}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="space-y-1 cursor-default">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{dimensionLabel(dim)}</span>
                            <span className="tabular-nums font-medium">{val}</span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-white/[0.08] overflow-hidden">
                            <div
                              className={cn(
                                "h-full rounded-full transition-all duration-700 ease-out",
                                val >= 80
                                  ? "bg-green-500"
                                  : val >= 50
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                              )}
                              style={{ width: animateBars ? `${String(Math.min(100, Math.max(0, val)))}%` : '0%' }}
                            />
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        {val >= 80
                          ? "This dimension is passing ✓"
                          : dimensionGuidance[dim] ?? "Improve this dimension to increase your score"}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            )}

            {/* Critical issues */}
            {score.criticalCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-red-400">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Critical Issues ({score.criticalCount})
                </div>
                {score.issues
                  .filter((i) => i.tier === "critical")
                  .map((issue) => (
                    <IssueCard key={issue.code} issue={issue} variant="critical" />
                  ))}
              </div>
            )}

            {/* Advisory issues */}
            {score.advisoryCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-yellow-400">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Advisory Issues ({score.advisoryCount})
                </div>
                {score.issues
                  .filter((i) => i.tier === "advisory")
                  .map((issue) => (
                    <IssueCard key={issue.code} issue={issue} variant="advisory" />
                  ))}
              </div>
            )}

            {/* Footer: last audited */}
            <div className="text-xs text-muted-foreground pt-1 border-t border-white/[0.06]">
              Last audited {formatDistanceToNow(new Date(score.auditedAt), { addSuffix: true })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
